import { Offer, OfferTransaction } from '@/api';
import BidTimer from '@/components/general/BidTimer';
import Button from '@/components/general/Button';
import Price from '@/components/general/Price';
import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useMutation } from '@tanstack/react-query';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import toast from 'react-hot-toast';
import { acceptOfferFn, rejectOfferFn } from '@/queries/offer';
import i18next from 'i18next';
import ExcMark from 'public/assets/icons/exclamation-mark-blue.svg';
import useOffers from '@/hooks/useOffers';
import useItemContext from '@/hooks/useItemContext';
import useAuth from '@/contexts/AuthContext';
import If, { Else, ElseIf } from '@/components/util/IF';
import ProductOfferModal from '../Modals/ProductOfferModal';
import ProductOffersModal from '../Modals/ProductOffersModal';
import DateFormat from '../../../../general/DateFormat';

type Props = {
    isEverythingLoaded: boolean;
    onShowOffering: (show: boolean) => void;
};

const ProductEcomPutOffer = ({ isEverythingLoaded, onShowOffering }: Props) => {
    const { item: product } = useItemContext();
    const { user } = useAuth();
    const { offerPossible, currentOffer } = useOffers(product, true);

    const [showOffers, setShowOffers] = useState<boolean>(false);
    const [showCounter, setShowCounter] = useState<boolean>(false);
    const itsYourItem = user && product?.owner_id === user?.id;
    const { invalidateQueries } = useOffers(product, false, false);

    const acceptCounterMutation = useMutation({
        mutationFn: acceptOfferFn,
        onSuccess: (response) => {
            invalidateQueries();
            if (response) {
                if (response.is_accepted && response.status === Offer.StatusEnum.CounterAccepted) {
                    toast.success(i18next.t('common:offers.congrats-purchased'));
                }
            }
        },
        onError: async (error) => {
            const transformedError = await ErrorHandler.transformError(error as Request);
            toast.error(transformedError.message);
        },

    });

    const declineOfferMutation = useMutation({
        mutationFn: rejectOfferFn,
        onSuccess: (response) => {
            invalidateQueries();
            if (response) {
                toast.success(i18next.t('common:offers.offer-declined'));
            }
        },
        onError: async (error) => {
            const transformedError = await ErrorHandler.transformError(error as Request);
            toast.error(transformedError.message);
        },

    });

    if ((!offerPossible && !currentOffer) || product?.current_offer_transaction?.status === OfferTransaction.StatusEnum.Pending || currentOffer?.is_accepted) {
        return null;
    }

    const yourTheLastOffer = user && currentOffer?.user_id === user?.id;
    const counterPending = itsYourItem && currentOffer?.status === Offer.StatusEnum.Countered;
    const showCounterToBuyer = !itsYourItem && yourTheLastOffer && currentOffer?.status === Offer.StatusEnum.Countered;
    const rejectedForSeller = itsYourItem && (currentOffer?.status === Offer.StatusEnum.CounterRejected || currentOffer?.status === Offer.StatusEnum.Rejected);
    const showPendingBannerToBuyer = yourTheLastOffer && !showCounterToBuyer && currentOffer?.status === Offer.StatusEnum.Pending;

    const offerRecieved = product && user && currentOffer && product?.owner_id === user?.id && currentOffer.status === Offer.StatusEnum.Pending;
    const lastSalePrice = !itsYourItem && !showCounterToBuyer && !product?.auto_accept_offer;
    const instantBuyNow = !itsYourItem && !showCounterToBuyer && product?.auto_accept_offer;
    const makeOfferInstant = !itsYourItem && !showCounterToBuyer && user;

    return (
        <If condition={!rejectedForSeller}>
            <If condition={!isEverythingLoaded}>
                <Skeleton width={410} height={54} className="mb-4" />
                <Else>
                    <div>
                        <div className="md:max-w-[480px] md:mx-0 mx-auto w-full sm-tablet:max-w-[unset] bg-grey16 rounded-[10px] p-[20px] mb-[40px]">
                            <If condition={offerRecieved}>
                                <div className="flex items-center text-[14px] font-[500] leading-[14px] py-[10px] px-2 justify-center  mb-4 bg-[#D1DEFF] text-[#3069FE] rounded-[8px]">
                                    <span className="w-[24px] font-[500] mr-[5px]"><ExcMark className="text-green5" /></span> {i18next.t('common:offers.new-offer-received')}
                                </div>
                            </If>
                            <If condition={showPendingBannerToBuyer}>
                                <div className="flex items-center text-[14px] leading-[17px] py-[10px] p-[16px] justify-center  mb-4 bg-[#ffe9b1] rounded-[8px]">
                                    {i18next.t('common:offers.already-submitted', { amount: currentOffer?.current_amount })} <DateFormat date={new Date(currentOffer?.expires_at ?? '')} />
                                </div>
                            </If>
                            <If condition={showCounterToBuyer}>
                                <div className="gap-[32px] flex flex-col">
                                    <div className="w-full border-b-[1px] border-grey14 pb-[16px]">
                                        <span className="text-[12px] weight-[500] text-grey15">{i18next.t('common:offers.counter-offer')}</span>
                                        <div className="flex flex-row justify-between">
                                            <Price className="text-[18px] font-[600]" justification="L" number={currentOffer?.current_amount ?? '0'} />
                                            <span className="text-[14px] font-[500] text-grey15"><DateFormat date={currentOffer?.updated_at ?? ''} /></span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[12px] weight-[500] text-grey15">{i18next.t('common:offers.counter-offer-expires')}</span>
                                        <div className="flex flex-row justify-between">
                                            <span className="text-[18px] font-[600]"><BidTimer dueDate={new Date(currentOffer?.expires_at ?? '')} /></span>
                                        </div>
                                    </div>
                                    <div>
                                        <If condition={counterPending}>
                                            <Button fullWidth disabled>{i18next.t('common:offers.awaiting-counter-response')}</Button>
                                        </If>
                                        <div className="flex flex-row gap-[10px] mt-[8px]">
                                            {currentOffer && <Button fullWidth loading={acceptCounterMutation.isLoading} onClick={() => acceptCounterMutation.mutateAsync(currentOffer.id)}>{i18next.t('common:offers.accept-counter')}</Button>}
                                            {currentOffer && (
                                                <Button light lightGray fullWidth disabled={acceptCounterMutation.isLoading} onClick={() => declineOfferMutation.mutateAsync(currentOffer.id)}>
                                                    <span className="text-red">{i18next.t('common:offers.decline-counter')}</span>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </If>

                            <If condition={itsYourItem}>
                                <div className="gap-[32px] flex flex-col">
                                    <div className="w-full border-b-[1px] border-grey14 pb-[16px]">
                                        <span className="text-[12px] weight-[500] text-grey15">{counterPending ? i18next.t('common:offers.counter-offer') : i18next.t('common:offers.offer')}</span>
                                        <div className="flex flex-row justify-between">
                                            <Price className="text-[18px] font-[600]" justification="L" number={currentOffer?.current_amount ?? '0'} />
                                            <span className="text-[14px] font-[500] text-grey15"><DateFormat date={counterPending ? currentOffer?.updated_at : currentOffer?.created_at ?? ''} /></span>
                                        </div>
                                        <div className="flex flex-row gap-1">
                                            <span className="text-[12px] font-[500] leading-[14px] text-grey15">{i18next.t('common:offers.estimated-payout')}:</span>
                                            <Price className="text-[14px] font-[500] leading-[14px]" justification="L" number={currentOffer?.estimated_payout_amount ?? '0'} />
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-[12px] weight-[500] text-grey15">{counterPending ? i18next.t('common:offers.counter') : i18next.t('common:offers.offer')} {i18next.t('common:offers.expires')}</span>
                                        <div className="flex flex-row justify-between">
                                            <span className="text-[18px] font-[600]"><BidTimer dueDate={new Date(currentOffer?.expires_at ?? '')} /></span>
                                            <span className="text-[14px] font-[500] text-grey15 underline cursor-pointer" onClick={() => setShowOffers(true)}>{i18next.t('common:offers.offer')}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <If condition={counterPending}>
                                            <Button fullWidth disabled>{i18next.t('common:offers.awaiting-counter-response')}</Button>
                                            <ElseIf condition={!counterPending && currentOffer?.status === Offer.StatusEnum.Pending}>
                                                {currentOffer && <Button loading={acceptCounterMutation.isLoading} fullWidth onClick={() => acceptCounterMutation.mutateAsync(currentOffer.id)}>{i18next.t('common:offers.accept-offer')}</Button>}
                                            </ElseIf>
                                        </If>
                                        <div className="flex flex-row gap-[10px] mt-[8px]">
                                            <Button light lightGray fullWidth disabled={counterPending || acceptCounterMutation.isLoading} onClick={() => setShowCounter(true)}>{i18next.t('common:offers.counter')}</Button>
                                            {currentOffer && (
                                                <Button light lightGray fullWidth disabled={counterPending || acceptCounterMutation.isLoading} onClick={() => declineOfferMutation.mutateAsync(currentOffer.id)}>
                                                    <span className="text-red">{i18next.t('common:offers.decline')}</span>
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </If>

                            <If condition={lastSalePrice}>
                                <div>
                                    <div>
                                        <span>{i18next.t('common:offers.last-sale-price')}</span>
                                        <If condition={product?.last_sold_amount && product?.last_sold_amount > '0.00'}>
                                            <Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={product?.last_sold_amount ?? '0'} />
                                            <Else>
                                                <div>-</div>
                                            </Else>
                                        </If>
                                    </div>
                                </div>
                            </If>

                            <If condition={instantBuyNow}>
                                <div>
                                    <div>
                                        <span>{i18next.t('common:offers.instant-buy-now')}</span>
                                        <If condition={product?.min_offer_amount && product?.min_offer_amount > '0.00'}>
                                            <Price className="@[600px]:text-[2rem] text-[1rem] font-[700]" justification="L" number={product?.min_offer_amount ?? '0'} />
                                            <Else>
                                                <div>-</div>
                                            </Else>
                                        </If>
                                    </div>
                                </div>
                            </If>

                        </div>
                        <If condition={makeOfferInstant}>
                            <Button
                                disabled={!!showPendingBannerToBuyer}
                                onClick={() => onShowOffering(true)}
                                fullWidth>{product?.auto_accept_offer ? i18next.t('common:offers.make-offer-instant') : i18next.t('common:offers.make-offer')}
                            </Button>
                        </If>

                        <ProductOffersModal show={showOffers} onHide={() => setShowOffers(false)} product={product} currentOffer={currentOffer} />
                        <ProductOfferModal offer={currentOffer} isCounter show={showCounter} onHide={() => setShowCounter(false)} product={product} />
                    </div>
                </Else>
            </If>
        </If>
    );
};

export default ProductEcomPutOffer;
